import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import intlTelInput from 'intl-tel-input'
import ScorecardSelector from '@v4/utils/selectors/ScorecardSelector'
import { createPortal } from 'react-dom'
import useWordings from '@v4/hooks/useWordings'
import { COUNTRY_CODES } from './PhoneNumber.const'
import withPreloadLibphonenumber from './withPreloadLibphonenumber'
import TextField from '../TextField'
import { isMobile } from './PhoneNumber.utils'
import useStyles from './PhoneNumber.styled'

const onChangeFallback = () => {}

const PhoneNumber = props => {
	const {
		name,
		value,
		variant,

		className,
		inputProps,
		InputProps,
		settings: { default_country: defaultCountry = '' },
		disabled = false,
		error = false,
		onChange = onChangeFallback,
		...rest
	} = props

	const { getWording } = useWordings('countries')
	const localeTextDirection = ScorecardSelector.useSettings()?.locale_text_direction || 'ltr'

	const classes = useStyles({ ...props, localeTextDirection })

	const dropdownRef = useRef(null)
	const portalDropdownRef = useRef(null)
	const inputRef = useRef(null)
	const itiRef = useRef(null)

	const localizedCountries = useMemo(() => {
		return _.reduce(
			COUNTRY_CODES,
			(acc, { country, code }) => {
				acc[code] = getWording(country)
				return acc
			},
			{},
		)
	}, [getWording])

	const handleChange = useCallback(() => {
		if (!itiRef.current) return

		onChange({ target: { name, value: itiRef.current.getNumber() } })
	}, [name, onChange])

	useEffect(() => {
		const inputEl = inputRef.current
		if (!inputEl) return undefined

		itiRef.current = intlTelInput(inputEl, {
			localizedCountries,
			nationalMode: true,
			preferredCountries: [],
			dropdownContainer: isMobile() ? portalDropdownRef.current : dropdownRef.current,
			initialCountry: defaultCountry || 'GB',
			utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.12/js/utils.js',
		})

		if (value) {
			itiRef.current.setNumber(value)
		}

		inputEl.addEventListener('countrychange', handleChange)

		return () => {
			inputEl?.removeEventListener('countrychange', handleChange)
			itiRef.current?.destroy()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultCountry, handleChange, name, localizedCountries])

	return (
		<>
			<TextField
				{...rest}
				variant={variant}
				name={name}
				className={clsx(classes.root, className)}
				classes={{
					focused: classes.focused,
					notchedOutline: classes.notchedOutline,
					transparent: classes.transparent,
				}}
				error={error}
				onChange={handleChange}
				type="tel"
				inputProps={{
					...inputProps,
					className: classes.input,
					ref: inputRef,
				}}
				InputProps={InputProps}
				disabled={disabled}
				focused
			/>
			<div className={clsx(classes.dropdown, classes.dropdownAbsolute)} ref={dropdownRef} />
			{createPortal(
				<div className={clsx(classes.dropdown, classes.dropdownFixed)} ref={portalDropdownRef} />,
				document.body,
			)}
		</>
	)
}

PhoneNumber.propTypes = {
	classes: PropTypes.shape({
		root: PropTypes.string,
		input: PropTypes.string,
	}),
	settings: PropTypes.oneOfType([
		PropTypes.shape({
			default_country: PropTypes.string,
		}),
	]),
	value: PropTypes.string,
	name: PropTypes.string.isRequired,
	disabled: PropTypes.bool,
	error: PropTypes.bool,
	variant: PropTypes.string,
	className: PropTypes.string,
	InputProps: PropTypes.object,
	inputProps: PropTypes.object,
	onChange: PropTypes.func,
	layout: PropTypes.string,
}

export default memo(withPreloadLibphonenumber(PhoneNumber, TextField))
